import React, { useEffect, useState } from "react";
import { ukNumeric } from "@/lib/time";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Building2, Handshake, CheckCircle2, XCircle, ClipboardCheck, ChevronRight, Zap } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ADMIN_COMMISSION_TYPES, COMMISSION_IS_PCT } from "./constants";
import { MoneyField, PercentField } from "@/components/ui/number-fields";
import CustomerAppsPanel from "./CustomerAppsPanel";
import { PurgeButton } from "@/components/hub/PurgeButton";

export default function HubApplications() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ lenders: [], introducers: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null); // {kind, app}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/hub/applications${tab === "all" ? "" : `?status=${tab}`}`);
      setData(r.data);
    } catch { setData({ lenders: [], introducers: [], total: 0 }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]);

  const total = data.lenders.length + data.introducers.length;

  return (
    <div className="space-y-6" data-testid="hub-applications">
      <div className="head">
        <div>
          <div className="eyebrow">Applications</div>
          <h1 className="t">Lender &amp; introducer applications</h1>
          <p className="mt-1 text-[13.5px] text-slate-500 max-w-2xl">
            Public applications from prospective lenders and introducers land here. Approve to auto-mint an invite; reject with a private reason.
          </p>
        </div>
        <Select value={tab} onValueChange={setTab}>
          <SelectTrigger className="w-40" data-testid="hub-apps-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <CustomerAppsPanel compact />

      <AutomationCard />

      {loading ? (
        <div className="grid place-items-center py-24 text-slate-400"><Loader2 className="size-6 animate-spin" /></div>
      ) : total === 0 ? (
        <div className="rounded-2xl border border-slate-200 border-dashed bg-white text-center py-20 px-6">
          <ClipboardCheck className="size-6 text-slate-400 mx-auto" />
          <div className="mt-3 text-[15px] font-semibold text-slate-900">No {tab === "all" ? "" : tab} applications</div>
          <p className="mt-1 text-[13.5px] text-slate-500 max-w-md mx-auto">Share <code className="text-slate-700 bg-slate-100 rounded px-1">/lender/apply</code> and <code className="text-slate-700 bg-slate-100 rounded px-1">/introducer/apply</code> to start receiving partner applications.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <ApplicationsColumn
            title="Lenders / Funders"
            Icon={Building2}
            items={data.lenders}
            onReview={(app) => setReviewing({ kind: "lender", app })}
          />
          <ApplicationsColumn
            title="Introducers"
            Icon={Handshake}
            items={data.introducers}
            onReview={(app) => setReviewing({ kind: "introducer", app })}
          />
        </div>
      )}

      <ReviewDialog
        open={!!reviewing}
        onOpenChange={(v) => !v && setReviewing(null)}
        kind={reviewing?.kind}
        app={reviewing?.app}
        onSaved={() => { setReviewing(null); load(); }}
      />
    </div>
  );
}

function AutomationCard() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => { api.get("/hub/settings").then((r) => setS(r.data)).catch(() => setS(null)); }, []);
  if (!s) return null;

  const flip = async (key, value) => {
    const prevValue = s[key];
    setSaving(key);
    setS((prev) => ({ ...prev, [key]: value }));
    try {
      const r = await api.patch("/hub/settings", { [key]: value });
      setS(r.data);
      toast.success(typeof value === "boolean" ? (value ? "Automation on" : "Automation off") : "Saved");
    } catch {
      setS((prev) => ({ ...prev, [key]: prevValue }));
      toast.error("Could not save that setting");
    } finally { setSaving(null); }
  };

  const Row = ({ id, title, body, extra }) => (
    <div className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <div className="text-[13.5px] font-semibold text-slate-900">{title}</div>
        <p className="mt-0.5 text-[12.5px] text-slate-500 leading-relaxed">{body}</p>
        {extra}
      </div>
      <Switch checked={!!s[id]} disabled={saving === id} onCheckedChange={(v) => flip(id, v)} data-testid={`automation-${id}`} />
    </div>
  );

  return (
    <div className="card p-5" data-testid="automation-card">
      <div className="flex items-center gap-2 text-[10.5px] font-semibold tracking-[0.22em] uppercase text-slate-600">
        <Zap className="size-3 text-[#0055FF]" /> Automation
      </div>
      <div className="mt-3 divide-y divide-slate-100">
        <Row id="auto_approve_referrals"
             title="Auto-approve invite & referral applications"
             body="Anyone arriving through a private invite or a partner's referral link is approved instantly — cold public applications still wait for you here." />
        <Row id="auto_issue_docs"
             title="Auto-issue agreement + NDA on activation"
             body="The moment a partner sets their password, both documents go out pre-signed by Connexis, by email and SMS, with reminders every 3 days until countersigned." />
        <Row id="notify_terms_changes"
             title="Tell partners when their rate changes"
             body="Change a commission and the partner gets a plain-English email the same moment — old rate, new rate, what happens next — with a copy of the agreement at the new terms attached." />
        <Row id="customer_apply_enabled"
             title="Customer application links"
             body="Introducers can send their customers a private link (or a QR code) to apply and upload documents themselves. Switch this off and every link politely closes." />
        <Row id="customer_apply_auto_route"
             title="Send customer applications straight to lenders"
             body="Off — and nothing a customer submits reaches a lender until you approve it under Customer apps. On, and it routes the moment it arrives." />
        <Row id="chat_alerts"
             title="Chase me when a partner is left waiting"
             body="If a partner messages the desk and nobody replies, you get an email and a text, repeated every 4 hours up to 3 times."
             extra={
               <div className="mt-2 flex items-center gap-2">
                 <span className="text-[11.5px] text-slate-500">Alert me after</span>
                 <Select value={String(s.chat_alert_after_minutes || 60)} onValueChange={(v) => flip("chat_alert_after_minutes", Number(v))}>
                   <SelectTrigger className="h-7 w-[112px] text-[12px]" data-testid="automation-chat-minutes"><SelectValue /></SelectTrigger>
                   <SelectContent>
                     {[15, 30, 60, 120, 240].map((m) => (
                       <SelectItem key={m} value={String(m)}>{m < 60 ? `${m} minutes` : m === 60 ? "1 hour" : `${m / 60} hours`}</SelectItem>
                     ))}
                   </SelectContent>
                 </Select>
               </div>
             } />
        <Row id="chat_auto_ack"
             title="Acknowledge a partner the second they send something"
             body="A partner who messages the desk — or drops a document into the chat — sees an instant receipt in the thread confirming it landed and which case it's filed against. It never counts as your reply: you're still chased if nobody answers." />
        <Row id="applicant_progress_emails"
             title="Keep the applicant in the loop"
             body="Every time a case actually moves — with lenders, being reviewed, offer made, funded — the applicant gets a short email and a link to their own tracking page. Declines are never emailed to a client: the introducer is told so they can break it personally." />
        <Row id="client_review_text"
             title="Text the client when a lender picks up their case"
             body="The first time any desk moves a case to In review, the client gets one text: a lender is reviewing it and will contact them directly by phone, email or text, so they pick up. Only 8am–8pm UK — later moves wait until 8am. Never names the lender; the text shows on the case thread for the introducer." />
        <Row id="chase_escalation"
             title="Nudge the introducer when documents go quiet"
             body="If a client hasn't uploaded within 3 days — or nobody has even asked them within 2 — the introducer gets a nudge with exactly what's outstanding. Up to 3 nudges, then it's a phone call." />
      </div>
    </div>
  );
}


function ApplicationsColumn({ title, Icon, items, onReview }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="size-4 text-slate-500" />
        <div className="text-[13px] font-semibold text-slate-800">{title}</div>
        <span className="text-[11px] text-slate-400">· {items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white py-8 text-center text-[13px] text-slate-500">
          None in this list.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((a) => <AppRow key={a.id} app={a} onReview={onReview} />)}
        </div>
      )}
    </div>
  );
}

function AppRow({ app, onReview }) {
  const p = app.payload || {};
  const name  = p.company_name || p.company || p.name || "—";
  const contact = p.contact_name || p.name || "—";
  const email = p.email || "—";
  const statusCls = {
    pending:  "bg-[#7da9ff]/[0.10]   border-[#7da9ff]/45   text-[#3B63A8]",
    approved: "bg-teal-50 border-teal-200 text-teal-700",
    rejected: "bg-slate-100    border-[#0B1220]/20 text-[#0B1220]",
  }[app.status || "pending"];
  return (
    <button type="button" onClick={() => onReview(app)}
            className="w-full rounded-xl border border-slate-200 bg-white p-3.5 text-left hover:border-slate-300 transition-colors flex items-start gap-3"
            data-testid={`hub-app-${app.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[13.5px] font-semibold text-slate-900 sm:truncate">{name}</div>
          <span className={`inline-flex items-center rounded-full border px-1.5 h-4 text-[9.5px] font-semibold uppercase tracking-wider ${statusCls}`}>{app.status}</span>
          {app.referred_by && (
            <span className="inline-flex items-center rounded-full border border-teal-200 bg-teal-50 text-teal-700 px-1.5 h-4 text-[9.5px] font-semibold uppercase tracking-wider" data-testid={`hub-app-referred-${app.id}`}>referred</span>
          )}
        </div>
        <div className="mt-0.5 text-[12px] text-slate-500 sm:truncate">{contact} · {email}</div>
        <div className="mt-1 text-[11.5px] text-slate-400 break-words">
          {(p.finance_types || []).slice(0, 3).join(" / ")}
          {(p.finance_types || []).length > 3 ? ` +${(p.finance_types || []).length - 3}` : ""}
        </div>
      </div>
      <ChevronRight className="size-4 text-slate-300 mt-1" />
    </button>
  );
}

function ReviewDialog({ open, onOpenChange, kind, app, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState("approve");
  const [commType, setCommType] = useState("revenue_share");
  const [lenderCommType, setLenderCommType] = useState("percentage");
  const [commPct, setCommPct] = useState("");
  const [commFlat, setCommFlat] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [overridePct, setOverridePct] = useState("");
  const [feePct, setFeePct] = useState("");

  useEffect(() => {
    if (open && app) {
      setAction(app.status === "pending" ? "approve" : "view");
      setCommType(app.payload?.preferred_commission_type || "revenue_share");
      setCommPct(""); setCommFlat(""); setRejectReason(""); setAdminNote(""); setOverridePct("");
      // A desk that told us its fee is priced off that fee — income share is the natural model.
      const stated = app.payload?.expected_fee_pct;
      setLenderCommType(stated != null && stated !== "" ? "income_share" : "percentage");
      setFeePct(stated != null ? String(stated) : "");
    }
  }, [open, app]);

  if (!app) return null;
  const p = app.payload || {};
  const isLender = kind === "lender";
  const readOnly = app.status !== "pending";

  const submit = async () => {
    setBusy(true);
    try {
      if (action === "reject") {
        await api.post(`/hub/applications/${kind}/${app.id}/reject`, { reason: rejectReason || null });
        toast.success("Application rejected");
      } else {
        const body = { admin_note: adminNote || null };
        if (isLender) {
          body.commission_type_lender = lenderCommType;
          if (lenderCommType === "flat") body.commission_flat_gbp_lender = parseFloat(commFlat) || 0;
          else body.commission_pct_lender = parseFloat(commPct) || 0;
          if (feePct !== "" && parseFloat(feePct) !== parseFloat(p.expected_fee_pct)) body.expected_fee_pct_lender = parseFloat(feePct) || 0;
        } else {
          body.commission_type = commType;
          if (COMMISSION_IS_PCT(commType)) body.commission_pct = parseFloat(commPct) || 0;
          else body.commission_flat_gbp = parseFloat(commFlat) || 0;
        }
        if (app.referred_by && overridePct !== "") body.override_pct = parseFloat(overridePct) || 0;
        await api.post(`/hub/applications/${kind}/${app.id}/approve`, body);
        toast.success(isLender ? "Lender created — invite generated" : "Introducer created — invite generated");
      }
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hub-app-review">
        <DialogHeader>
          <DialogTitle>{isLender ? "Lender application" : "Introducer application"} · {p.company_name || p.company || p.name}</DialogTitle>
          <DialogDescription>Submitted {ukNumeric(app.created_at)} · {app.status}</DialogDescription>
        </DialogHeader>

        {/* Details */}
        <div className="grid sm:grid-cols-2 gap-3 text-[13px]">
          <Detail label="Contact"    value={p.contact_name || p.name} />
          <Detail label="Email"      value={p.email} />
          <Detail label="Phone"      value={p.phone} />
          <Detail label="Company"    value={p.company_name || p.company} />
          {p.company_number && <Detail label="Companies House #" value={p.company_number} />}
          {p.website && <Detail label="Website"   value={p.website} />}
          {isLender && <Detail label="Firm type" value={p.company_type} />}
          {isLender && p.min_deal_size_gbp !== undefined && <Detail label="Deal size" value={`£${(p.min_deal_size_gbp || 0).toLocaleString()}–£${(p.max_deal_size_gbp || 0).toLocaleString() || "∞"}`} />}
          {isLender && p.turnaround_days !== undefined && <Detail label="Turnaround" value={`${p.turnaround_days ?? "—"} days`} />}
          {isLender && p.monthly_capacity_gbp && <Detail label="Monthly capacity" value={`£${p.monthly_capacity_gbp.toLocaleString()}`} />}
          {isLender && <Detail label="Their typical fee" value={p.expected_fee_pct != null ? `${p.expected_fee_pct}% of facility · stated by the desk` : "Not given — ask, or set a forecast below"} />}
          {!isLender && p.fca_regulated !== undefined && <Detail label="FCA regulated" value={p.fca_regulated ? `Yes · ${p.fca_number || "no number"}` : "No"} />}
          {!isLender && p.years_active !== undefined && <Detail label="Years active" value={String(p.years_active)} />}
          {!isLender && p.monthly_volume_estimate && <Detail label="Monthly volume" value={p.monthly_volume_estimate} />}
          {!isLender && <Detail label="Preferred commission" value={p.preferred_commission_type?.replace("_", " ")} />}
          {app.referred_by_name && <Detail label="Referred by" value={`${app.referred_by_name} · network partner`} />}
          {p.address && <Detail label="Address" value={p.address} full />}
          {(p.finance_types || []).length > 0 && <Detail label="Finance types" value={(p.finance_types || []).join(", ")} full />}
          {(p.industry_sectors || []).length > 0 && <Detail label="Sectors" value={(p.industry_sectors || []).join(", ")} full />}
          {(p.countries_covered || []).length > 0 && <Detail label={isLender ? "Countries funded" : "Countries covered"} value={(p.countries_covered || []).join(", ")} full />}
          {p.lending_criteria && <Detail label="Lending criteria" value={p.lending_criteria} full />}
          {p.notes && <Detail label="Notes" value={p.notes} full />}
        </div>

        {/* Action */}
        {!readOnly ? (
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center gap-2 mb-3">
              <button type="button" onClick={() => setAction("approve")} className={`inline-flex items-center gap-1.5 rounded-[10px] px-3 h-8 text-[12px] font-semibold border ${action === "approve" ? "bg-teal-500 border-teal-500 text-white" : "bg-white border-slate-300 text-slate-700"}`} data-testid="review-approve"><CheckCircle2 className="size-3.5" /> Approve</button>
              <button type="button" onClick={() => setAction("reject")}  className={`inline-flex items-center gap-1.5 rounded-[10px] px-3 h-8 text-[12px] font-semibold border ${action === "reject"  ? "bg-red-500 border-red-500 text-white"       : "bg-white border-slate-300 text-slate-700"}`} data-testid="review-reject"><XCircle className="size-3.5" /> Reject</button>
            </div>
            {action === "approve" ? (
              isLender ? (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600">Connexis fee model</Label>
                    <div className="mt-1.5 flex gap-1.5">
                      {[["income_share", "% of their fee"], ["percentage", "% of funded"], ["flat", "Flat £ / funded"]].map(([k, l]) => (
                        <button key={k} type="button" onClick={() => setLenderCommType(k)}
                                className={`flex-1 rounded-lg border px-2 h-9 text-[12px] font-semibold transition-colors ${lenderCommType === k ? "bg-[#0055FF]/[0.06] border-[#0055FF]/50 text-[#0055FF]" : "bg-white border-slate-300 text-slate-600 hover:border-slate-400"}`}
                                data-testid={`review-lender-comm-${k}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                  {lenderCommType !== "flat" ? (
                    <div>
                      <Label className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600">
                        {lenderCommType === "income_share" ? "Our share of the fee they earn" : "Commission % on funded completions"}
                      </Label>
                      <PercentField value={commPct} onChange={setCommPct} max={100}
                                    presets={lenderCommType === "income_share" ? [25, 40, 50, 60, 75] : [2, 5, 10, 15, 20]} testId="review-comm-pct" />
                    </div>
                  ) : (
                    <div>
                      <Label className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600">Flat fee (£ per funded deal)</Label>
                      <MoneyField value={commFlat} onChange={setCommFlat} ladder="fee" compact testId="review-comm-flat" hint="per funded deal" />
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <Label className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600">What they usually earn on a deal</Label>
                    <PercentField value={feePct} onChange={setFeePct} max={20} step={0.25} presets={[1, 1.5, 2.5, 5]} testId="review-expected-fee"
                                  hint={p.expected_fee_pct != null ? `the desk stated ${p.expected_fee_pct}% on their application — carried in as-is unless you change it` : "forecast only — the real figure is confirmed per deal"} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600">Admin note (internal)</Label>
                    <Textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} className="min-h-[60px]" />
                  </div>
                  {app.referred_by && <OverrideField name={app.referred_by_name} value={overridePct} onChange={setOverridePct} />}
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600">Commission model</Label>
                    <div className="mt-1.5 grid sm:grid-cols-2 gap-2">
                      {ADMIN_COMMISSION_TYPES.map((c) => (
                        <button key={c.key} type="button" onClick={() => setCommType(c.key)}
                                className={`text-left rounded-lg border p-2.5 transition-colors ${commType === c.key ? "bg-[#0055FF]/[0.06] border-[#0055FF]/50" : "bg-white border-slate-300 hover:border-slate-400"}`}
                                data-testid={`review-comm-${c.key}`}>
                          <div className="text-[12.5px] font-semibold text-slate-900">{c.label}</div>
                          <div className="text-[10.5px] text-slate-500 mt-0.5 leading-snug">{c.hint}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {COMMISSION_IS_PCT(commType) ? (
                    <div>
                      <Label className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600">
                        {commType === "commission_share" ? "% of our commission" : "% of funded amount"}
                      </Label>
                      <PercentField value={commPct} onChange={setCommPct} max={100}
                                    presets={commType === "commission_share" ? [25, 40, 50, 60, 75] : [1.5, 5, 10, 20, 50]}
                                    testId="review-comm-pct" />
                    </div>
                  ) : (
                    <div>
                      <Label className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600">Fixed amount (£)</Label>
                      <MoneyField value={commFlat} onChange={setCommFlat} ladder="fee" compact testId="review-comm-flat" hint="per funded deal / lead" />
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <Label className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600">Admin note (internal)</Label>
                    <Textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} className="min-h-[60px]" />
                  </div>
                  {app.referred_by && <OverrideField name={app.referred_by_name} value={overridePct} onChange={setOverridePct} />}
                </div>
              )
            ) : (
              <div>
                <Label className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600">Reason (private, not sent to applicant)</Label>
                <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="min-h-[80px] mt-1.5" placeholder="e.g. Doesn't meet minimum monthly capacity" data-testid="review-reject-reason" />
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-[13px] text-slate-600">
            {app.status === "approved" ? "Approved. Invite email was sent to the applicant." : `Rejected${app.reject_reason ? ` — ${app.reject_reason}` : ""}.`}
          </div>
        )}

        <DialogFooter>
          <PurgeButton kind={kind === "lender" ? "lender_application" : "introducer_application"}
                       ids={[app.id]} label="Delete application"
                       onDone={() => { onOpenChange(false); onSaved?.(); }}
                       className="mr-auto" testId="review-purge" />
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {!readOnly && (
            <Button onClick={submit} disabled={busy} className={action === "reject" ? "bg-red-500 hover:bg-red-600" : "bg-teal-500 hover:bg-teal-600"} data-testid="review-submit">
              {busy ? <Loader2 className="size-4 animate-spin" /> : (action === "reject" ? "Reject application" : "Approve & generate invite")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OverrideField({ name, value, onChange }) {
  return (
    <div className="sm:col-span-2 rounded-lg border border-teal-200 bg-teal-50 p-3">
      <Label className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-teal-800">Network override % for {name || "the recruiter"}</Label>
      <div className="mt-1.5 flex items-center gap-3 flex-wrap">
        <div className="w-52"><PercentField value={value} onChange={onChange} max={100} step={0.5} testId="review-override-pct" hint="blank = their default" /></div>
        <span className="text-[11.5px] text-teal-700 leading-snug max-w-sm">
          Paid by Connexis on top — the recruit keeps 100% of their own rate. Leave blank to apply the recruiter&apos;s chosen rate.
        </span>
      </div>
    </div>
  );
}

function Detail({ label, value, full = false }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{label}</div>
      <div className="text-[13px] text-slate-900 mt-0.5 break-words">{value}</div>
    </div>
  );
}
